import { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'wouter';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, Loader2, AlertCircle, Check, Clock, Users, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FormField } from '@/components/ui/form-field';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { organizationService } from '@/services/api/organization.service';
import type { OrganizationRegistration } from '@/types/organization';
import { apiClient } from '@/lib/api-client';
import { GoogleSignInButton } from '@/components/auth/google-sign-in-button';
import { sanitizeErrorMessage, getErrorMessageByStatus } from '@/lib/error-utils';
import { pickRegistrationLocale } from '@/lib/registrationLocale';
import { validateEmail } from '@/lib/email-validator';
import { suggestEmail } from '@/lib/email-typo';
import { AuthPageLayout } from '@/components/auth/auth-page-layout';
import { inviteCodeFromSearch } from '@/lib/invite-code';

// Helper to get browser timezone
const getBrowserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'America/New_York'; // fallback
  }
};

export default function RegisterPage() {
  const { t, i18n } = useTranslation(['registration', 'common', 'validation']);

  const registrationSchema = z.object({
    business_name: z.string()
      .min(2, t('validation:businessName.min'))
      .max(100, t('validation:businessName.max')),
    email: z.string().min(1, t('validation:email.invalid')).superRefine((val, ctx) => {
      const result = validateEmail(val);
      if (result.ok) return;
      const msg =
        result.code === 'format'
          ? t('validation:email.invalid_format', { defaultValue: 'Please enter a valid email address' })
          : result.code === 'typo'
            ? (result.suggestion
                ? t('validation:email.suspicious_typo', { suggestion: result.suggestion, defaultValue: `Did you mean ${result.suggestion}?` })
                : t('validation:email.suspicious_typo_generic', { defaultValue: 'This email looks like a typo' }))
            : t('validation:email.disposable', { defaultValue: 'Disposable email providers are not allowed' });
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: msg });
    }),
    password: z.string()
      .min(8, t('validation:password.min'))
      .regex(/[A-Z]/, t('validation:password.uppercase'))
      .regex(/[a-z]/, t('validation:password.lowercase'))
      .regex(/[0-9]/, t('validation:password.number')),
    password_confirmation: z.string(),
    subdomain: z.string()
      .min(3, t('validation:subdomain.min'))
      .max(50, t('validation:subdomain.max'))
      .regex(/^[a-z0-9-]+$/, t('validation:subdomain.format')),
  }).refine((data) => data.password === data.password_confirmation, {
    message: t('validation:password.match'),
    path: ["password_confirmation"],
  });

  type RegistrationFormData = z.infer<typeof registrationSchema>;

  const [, setLocation] = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);
  const [isCheckingSubdomain, setIsCheckingSubdomain] = useState(false);
  const [subdomainAvailable, setSubdomainAvailable] = useState<boolean | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const [userEditedSubdomain, setUserEditedSubdomain] = useState(false);

  // Set by the invite-only landing pages (/register?invitation_code=XXXX). It is
  // never shown as an editable field: it travels as a hidden value and is
  // re-validated server-side at signup.
  const invitationCode = useMemo(() => inviteCodeFromSearch(window.location.search), []);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
    setError
  } = useForm<RegistrationFormData>({
    resolver: zodResolver(registrationSchema),
    defaultValues: {
      business_name: '',
      email: '',
      password: '',
      password_confirmation: '',
      subdomain: '',
    }
  });

  const subdomain = watch('subdomain');
  const businessName = watch('business_name');

  // Auto-generate subdomain suggestion from business name in real-time.
  // Mirrors the backend i18n.transliterate.rule entries in config/locales/*.yml
  // so the live preview matches what Organization#generate_subdomain produces.
  useEffect(() => {
    if (businessName && !userEditedSubdomain) {
      const diacriticsMap: Record<string, string> = {
        // Polish
        'ą': 'a', 'ć': 'c', 'ę': 'e', 'ł': 'l', 'ń': 'n', 'ó': 'o', 'ś': 's', 'ź': 'z', 'ż': 'z',
        'Ą': 'a', 'Ć': 'c', 'Ę': 'e', 'Ł': 'l', 'Ń': 'n', 'Ó': 'o', 'Ś': 's', 'Ź': 'z', 'Ż': 'z',
        // German
        'ä': 'a', 'ö': 'o', 'ü': 'u', 'ß': 'ss',
        'Ä': 'a', 'Ö': 'o', 'Ü': 'u',
        // Portuguese
        'á': 'a', 'â': 'a', 'ã': 'a', 'à': 'a', 'ç': 'c', 'é': 'e', 'ê': 'e', 'í': 'i', 'ô': 'o', 'õ': 'o', 'ú': 'u',
        'Á': 'a', 'Â': 'a', 'Ã': 'a', 'À': 'a', 'Ç': 'c', 'É': 'e', 'Ê': 'e', 'Í': 'i', 'Ô': 'o', 'Õ': 'o', 'Ú': 'u',
      };
      const suggested = businessName
        .replace(/[ąćęłńóśźżĄĆĘŁŃÓŚŹŻäöüßÄÖÜáâãàçéêíôõúÁÂÃÀÇÉÊÍÔÕÚ]/g, (c) => diacriticsMap[c] ?? c)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
        .substring(0, 50);
      setValue('subdomain', suggested);
    }
  }, [businessName, userEditedSubdomain, setValue]);

  // Track when user manually edits subdomain - normalize to lowercase
  const handleSubdomainChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserEditedSubdomain(true);
    setValue('subdomain', e.target.value.toLowerCase());
  };

  // Check subdomain availability using actual API
  useEffect(() => {
    const normalizedSubdomain = subdomain?.toLowerCase().trim();

    if (!normalizedSubdomain || normalizedSubdomain.length < 3) {
      setSubdomainAvailable(null);
      return;
    }

    if (!/^[a-z0-9]+(-[a-z0-9]+)*$/.test(normalizedSubdomain)) {
      setSubdomainAvailable(null);
      return;
    }

    const timer = setTimeout(async () => {
      setIsCheckingSubdomain(true);
      try {
        const result = await organizationService.checkSubdomainAvailability(normalizedSubdomain);
        setSubdomainAvailable(result.available);
      } catch (error) {
        console.error('Failed to check subdomain availability:', error);
        setSubdomainAvailable(null);
      } finally {
        setIsCheckingSubdomain(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [subdomain]);

  const getFriendlyErrorMessage = (error: any): string => {
    const status = error.response?.status;

    if (status === 422) {
      const serverMessage = sanitizeErrorMessage(error, '');

      if (serverMessage.toLowerCase().includes('subdomain')) {
        return 'This subdomain is already taken. Please choose a different one.';
      }
      if (serverMessage.toLowerCase().includes('email')) {
        return 'An account with this email already exists. Please use a different email or try logging in.';
      }
      if (serverMessage.toLowerCase().includes('password')) {
        return 'Password does not meet security requirements. Please ensure it has at least 8 characters with uppercase, lowercase, and numbers.';
      }
      return 'Please check the form for errors and try again.';
    }

    if (!navigator.onLine) {
      return 'No internet connection. Please check your network and try again.';
    }

    if (status) {
      return getErrorMessageByStatus(status, 'We couldn\'t create your account. Please check your information and try again.');
    }

    return sanitizeErrorMessage(error, 'We couldn\'t create your account. Please check your information and try again.');
  };

  const onSubmit = async (data: RegistrationFormData) => {
    try {
      setApiError(null);
      const locale = pickRegistrationLocale(i18n.language);

      // Build registration data: send email as both business_email and owner_email
      const registrationData: OrganizationRegistration = {
        business_name: data.business_name,
        business_email: data.email,
        owner_email: data.email,
        password: data.password,
        password_confirmation: data.password_confirmation,
        subdomain: data.subdomain.toLowerCase().trim(),
        timezone: getBrowserTimezone(),
        ...(invitationCode ? { invitation_code: invitationCode } : {}),
        // Currency is detected server-side from the visitor's IP (CF header → ip-api),
        // so it stays consistent with the landing page and isn't skewed by browser locale.
        locale,
      };

      const response = await organizationService.register(registrationData);

      // Store JWT token using apiClient
      if (response.token) {
        apiClient.setAuthTokens(response.token, response.token);

        if (response.organization) {
          localStorage.setItem('organization', JSON.stringify(response.organization));
        }

        // Use full page redirect to trigger AuthContext re-initialization
        window.location.href = '/admin/onboarding';
      }
    } catch (error: any) {
      console.error('Registration failed:', error);

      const backendErrors = error.response?.data?.error?.errors || error.response?.data?.errors;
      const backendMessage = error.response?.data?.error?.message || error.response?.data?.message;

      if (backendErrors && typeof backendErrors === 'object' && Object.keys(backendErrors).length > 0) {
        Object.keys(backendErrors).forEach((field) => {
          let message = Array.isArray(backendErrors[field])
            ? backendErrors[field].join(', ')
            : backendErrors[field];

          message = String(message).replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();

          // Map backend field names to simplified form field names
          const fieldMap: Record<string, keyof RegistrationFormData> = {
            business_email: 'email',
            owner_email: 'email',
            business_name: 'business_name',
            subdomain: 'subdomain',
            password: 'password',
            password_confirmation: 'password_confirmation',
          };

          const formField = fieldMap[field] || field;
          if (['business_name', 'email', 'password', 'password_confirmation', 'subdomain'].includes(formField)) {
            setError(formField as keyof RegistrationFormData, {
              type: 'server',
              message
            });
          }
        });
        setApiError('Please review and correct the errors in the form below.');
      } else if (backendMessage) {
        setApiError(sanitizeErrorMessage(error, backendMessage));
      } else {
        const friendlyMessage = getFriendlyErrorMessage(error);
        setApiError(friendlyMessage);
      }
    }
  };

  // Live value of the subdomain field, so the helper can echo the real address
  // back rather than describing it in the abstract.
  const subdomainValue = watch('subdomain');

  // Benefits data with icons
  const benefits = [
    { icon: Clock, title: t('registration:marketing.benefits.bookings247'), desc: t('registration:marketing.benefits.bookings247Desc') },
    { icon: Users, title: t('registration:marketing.benefits.teamManagement'), desc: t('registration:marketing.benefits.teamManagementDesc') },
    { icon: Heart, title: t('registration:marketing.benefits.delightCustomers'), desc: t('registration:marketing.benefits.delightCustomersDesc') },
  ];

  return (
    <AuthPageLayout documentTitle={t('registration:title')}>
      <div className="mx-auto w-full max-w-4xl">
        {/*
          No marketing below lg. The old layout used flex-col-reverse, which put
          the whole panel AFTER the form — five fields of scrolling before the
          reason to fill them in. Replacing it with a tinted bullet list above
          the form was not much better: a second card competing with the real
          one, making claims before the page had said what it was, for ~220px of
          a phone screen. People arriving here have come from a marketing page
          and have had the pitch; on a phone the form's job is to be short.
        */}
        <div className="grid overflow-hidden rounded-2xl border border-line bg-surface shadow-card lg:grid-cols-[0.85fr_1fr]">
          {/*
            Light tint rather than a gradient. The old panel was
            from-blue-500 via-blue-600 to-blue-900 — Tailwind defaults, where
            #1E3A8A is not a Sloty colour at all (--brand-900 is #172554) —
            stretched to the form's height with its content floated in the
            middle, which left a large empty field below it.
          */}
          <div className="hidden border-r border-brand-200 bg-brand-50 p-8 lg:flex lg:flex-col lg:justify-center lg:gap-10">
            <div>
              <h2 className="text-h1 text-ink">{t('registration:marketing.headline')}</h2>
              <p className="mt-3 text-body-lg text-brand-700">
                {t('registration:marketing.subheadline')}
              </p>
            </div>

            <div className="flex flex-col gap-7">
              {benefits.map((benefit) => (
                <div key={benefit.title} className="flex items-start gap-3">
                  <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border border-brand-200 bg-surface text-brand-500">
                    <benefit.icon className="h-4 w-4" aria-hidden="true" />
                  </span>
                  <div>
                    <h3 className="text-h4 text-ink">{benefit.title}</h3>
                    <p className="mt-1 text-body-sm text-soft">{benefit.desc}</p>
                  </div>
                </div>
              ))}
            </div>

          </div>

          <div className="p-5 sm:p-7">
            <div className="mb-5">
              <h1 className="text-h2 text-ink">{t('registration:title')}</h1>
              <p className="mt-1 text-body-sm text-soft">
                {t('registration:subtitle')}{' '}
                {t('registration:loginPrompt.text')}{' '}
                <a href="/login" className="font-medium text-brand-500 hover:text-brand-700">
                  {t('registration:loginPrompt.link')}
                </a>
              </p>
            </div>

            {/*
              Signing up with Google also skips the password fields entirely —
              the business name and booking address are collected on a short
              follow-up step once Google has verified who you are.
            */}
            <GoogleSignInButton flow="register" showDivider={false} invitationCode={invitationCode} />

            <div className="my-5 flex items-center gap-4">
              <span className="h-px flex-1 bg-line" />
              <span className="text-xs uppercase tracking-wide text-soft">
                {t('registration:oauth.divider')}
              </span>
              <span className="h-px flex-1 bg-line" />
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              {invitationCode && (
                <input type="hidden" name="invitation_code" value={invitationCode} data-testid="invitation-code" />
              )}
              {apiError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{apiError}</AlertDescription>
                </Alert>
              )}

              <FormField
                label={t('registration:form.businessName')}
                error={errors.business_name?.message}
              >
                <Input
                  id="business_name"
                  inputSize="lg"
                  {...register('business_name')}
                  placeholder={t('registration:form.businessNamePlaceholder')}
                  autoFocus
                />
              </FormField>

              <FormField
                label={
                  <span className="inline-flex items-center gap-2">
                    {t('registration:form.subdomain')}
                    {isCheckingSubdomain && (
                      <span className="text-sm text-soft">{t('registration:subdomain.checking')}</span>
                    )}
                    {!isCheckingSubdomain && subdomainAvailable === true && (
                      <span className="text-sm text-success inline-flex items-center">
                        <Check className="h-3 w-3 mr-1" /> {t('registration:subdomain.available')}
                      </span>
                    )}
                    {!isCheckingSubdomain && subdomainAvailable === false && (
                      <span className="text-sm text-danger">{t('registration:subdomain.notAvailable')}</span>
                    )}
                  </span>
                }
                /*
                  Echo the value back as the real address instead of describing
                  it. This is the only field on the form whose consequence is
                  permanent and public, and it was the least explained.
                */
                helperText={
                  subdomainValue
                    ? t('registration:subdomain.preview', { url: `${subdomainValue}.slotyapp.com` })
                    : t('registration:subdomain.hint')
                }
                error={errors.subdomain?.message}
              >
                <div className="flex items-stretch">
                  <Input
                    id="subdomain"
                    inputSize="lg"
                    {...register('subdomain')}
                    onChange={handleSubdomainChange}
                    placeholder={t('registration:form.subdomainPlaceholder')}
                    className="rounded-r-none"
                  />
                  <span className="inline-flex items-center whitespace-nowrap rounded-r-md border border-l-0 border-line bg-surface-2 px-3 text-sm text-soft">
                    .slotyapp.com
                  </span>
                </div>
              </FormField>

              <FormField label={t('registration:form.email')} error={errors.email?.message}>
                <Input
                  id="email"
                  type="email"
                  inputSize="lg"
                  {...register('email')}
                  placeholder={t('registration:form.emailPlaceholder')}
                />
                {(() => {
                  const v = watch('email');
                  const s = v ? suggestEmail(v) : null;
                  return s && s !== v.trim() ? (
                    <button
                      type="button"
                      className="text-sm text-brand-500 underline mt-1"
                      onClick={() => setValue('email', s, { shouldValidate: true })}
                    >
                      {t('validation:email.didYouMean', { suggestion: s, defaultValue: `Did you mean ${s}?` })}
                    </button>
                  ) : null;
                })()}
              </FormField>

              <FormField
                label={t('registration:form.password')}
                helperText={t('registration:password.hint')}
                error={errors.password?.message}
              >
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    inputSize="lg"
                    {...register('password')}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={t('registration:form.togglePassword')}
                    className="absolute inset-y-0 right-0 flex items-center pr-3"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-soft" />
                    ) : (
                      <Eye className="h-4 w-4 text-soft" />
                    )}
                  </button>
                </div>
              </FormField>

              {/*
                No placeholder here. The old one repeated "Min. 8 characters",
                which is the wrong instruction for a confirm field and was
                already stated as helper text on the field above.
              */}
              <FormField
                label={t('registration:form.confirmPassword')}
                error={errors.password_confirmation?.message}
              >
                <div className="relative">
                  <Input
                    id="password_confirmation"
                    type={showPasswordConfirmation ? 'text' : 'password'}
                    inputSize="lg"
                    {...register('password_confirmation')}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswordConfirmation(!showPasswordConfirmation)}
                    aria-label={t('registration:form.togglePassword')}
                    className="absolute inset-y-0 right-0 flex items-center pr-3"
                  >
                    {showPasswordConfirmation ? (
                      <EyeOff className="h-4 w-4 text-soft" />
                    ) : (
                      <Eye className="h-4 w-4 text-soft" />
                    )}
                  </button>
                </div>
              </FormField>

              <Button
                type="submit"
                size="lg"
                disabled={isSubmitting || subdomainAvailable === false}
                className="w-full"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('registration:buttons.creating')}
                  </>
                ) : (
                  t('registration:buttons.submit')
                )}
              </Button>

              <p className="text-center text-body-xs text-soft">
                {t('registration:legal.prefix')}{' '}
                <a
                  href="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-500 underline hover:text-brand-700"
                >
                  {t('registration:legal.terms')}
                </a>{' '}
                {t('registration:legal.and')}{' '}
                <a
                  href="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-500 underline hover:text-brand-700"
                >
                  {t('registration:legal.privacy')}
                </a>
              </p>
            </form>
          </div>
        </div>
      </div>
    </AuthPageLayout>
  );
}
